
import { normalizeDateYMD } from './manager-utils.js';
import { debugMode } from './filter-logic-config.js';
import { favFilterOn, favHas } from '../../favorites/favorites-state.js';

/**
 * Apply category (L/F/M/S), public-only, and UI date filters to file list.
 * Runs BEFORE search query (see CLAUDE.md "Critical Filter Application Order").
 * @param {Array} files - Source file list
 * @param {Object} activeFilters - filterManager.activeFilters (used for publicOnly)
 * @param {Object} uiFilters - UI-derived filters (used for dateFrom/dateTo)
 * @param {boolean} queryChanged - Whether the user query changed (gates verbose logs)
 * @returns {Array} Filtered files
 */
export function applyPrefilters(files, activeFilters, uiFilters, queryChanged) {
    const filesBeforeFilters = files.length;
    let filteredFiles = applyCategoryFilters(files);

    if (debugMode === '1' && filesBeforeFilters !== filteredFiles.length) {
        console.log('[SEARCH] Category filters total:', filesBeforeFilters, '->', filteredFiles.length);
    }

    if (activeFilters.publicOnly) {
        filteredFiles = filteredFiles.filter(file => !!(file.pu === 1));
    }

    // Favorites-only toggle (star in the filters bar); identity key = decoded rel
    // path, same as the star rail (favorites-state.js).
    if (favFilterOn()) {
        const beforeFavFilter = filteredFiles.length;
        filteredFiles = filteredFiles.filter(file => {
            const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
            const n = (file.n || file.name || '');
            return favHas(`${p}${p ? '/' : ''}${n}`);
        });
        if (debugMode === '1' && beforeFavFilter !== filteredFiles.length) {
            console.log('[SEARCH] Favorites filter applied:', beforeFavFilter, '->', filteredFiles.length);
        }
    }

    filteredFiles = applyUiDateFilters(filteredFiles, uiFilters, queryChanged);

    if (debugMode === '1' && queryChanged && filesBeforeFilters !== filteredFiles.length) {
        console.log('[SEARCH] Category filters applied:', filesBeforeFilters, '->', filteredFiles.length);
    }

    return filteredFiles;
}

function applyCategoryFilters(files) {
    if (!window.filterController || typeof window.filterController.getSelectedCategories !== 'function') {
        return files;
    }
    const propertyFilters = window.filterController.getSelectedCategories() || {};
    if (debugMode === '1') {
        console.log('[SEARCH] Category filters:', propertyFilters);
    }

    let result = files;
    for (const group of ['L', 'F', 'M', 'S']) {
        const selectedValues = propertyFilters[group];
        if (Array.isArray(selectedValues) && selectedValues.length > 0) {
            const beforeGroupFilter = result.length;
            result = result.filter(file => selectedValues.includes(file[group] || 'empty'));
            if (debugMode === '1' && beforeGroupFilter !== result.length) {
                console.log('[SEARCH] Category filter', group, 'applied:', beforeGroupFilter, '->', result.length, 'values:', selectedValues);
            }
        }
    }
    return result;
}

function applyUiDateFilters(filteredFiles, uiFilters, queryChanged) {
    const dateFromRaw = (typeof uiFilters.dateFrom === 'string') ? uiFilters.dateFrom.trim() : '';
    const dateToRaw = (typeof uiFilters.dateTo === 'string') ? uiFilters.dateTo.trim() : '';
    const dateFromUI = dateFromRaw ? normalizeDateYMD(dateFromRaw) : null;
    const dateToUI = dateToRaw ? normalizeDateYMD(dateToRaw) : null;

    if (debugMode === '1' && queryChanged && (dateFromRaw || dateToRaw)) {
        console.log('[SEARCH] Date filters raw values:', { dateFromRaw, dateToRaw, dateFromUI, dateToUI });
    }

    if (!dateFromUI && !dateToUI) {
        if (debugMode === '1' && queryChanged && (dateFromRaw || dateToRaw)) {
            console.log('[SEARCH] Date filters normalization failed:', { dateFromRaw, dateToRaw });
        }
        return filteredFiles;
    }

    const filesBeforeDateFilter = filteredFiles.length;
    const result = filteredFiles.filter(file => {
        const fileDate = file.d || '';
        if (!fileDate) return false; // Files without dates excluded from date-filtered results
        if (dateFromUI && fileDate < dateFromUI) return false;
        if (dateToUI && fileDate > dateToUI) return false;
        return true;
    });

    if (debugMode === '1' && queryChanged && filesBeforeDateFilter !== result.length) {
        console.log('[SEARCH] Date filters applied:', filesBeforeDateFilter, '->', result.length, { dateFrom: dateFromUI, dateTo: dateToUI });
    }
    return result;
}
